import { combineReducers } from 'redux';
import { createReducer } from 'redux-act';
import { createSelector } from 'reselect';
import { createResource } from '@gandi/resource/redux';
import { toQueryParameter, statePath as moduleStatePath, baseUrl as moduleBaseUrl } from '../../utils';
import { selectRaw as moduleSelectRaw } from '../common';
export const statePath = `${moduleStatePath}.simplehosting.instances`;
const baseUrl = `${moduleBaseUrl}/paas`;
const initialPricesState = () => ({});
const initialTrialState = () => ({});
const initialTaxesState = () => [];
const convertApiPriceToState = price => {
  let ret = {
    maxDuration: price.max_duration,
    minDuration: price.min_duration,
    durationUnit: price.duration_unit,
    discount: price.discount,
    price: price.price,
    priceBeforeTaxes: price.price_before_taxes,
    priceAfterTaxes: price.price_after_taxes,
    features: price.features
  };
  if ('normal_price_before_taxes' in price) {
    ret = {
      ...ret,
      normalPriceBeforeTaxes: price.normal_price_before_taxes
    };
  }
  if ('normal_price_after_taxes' in price) {
    ret = {
      ...ret,
      normalPriceAfterTaxes: price.normal_price_after_taxes
    };
  }
  if ('trial' in price.options) {
    return {
      ...ret,
      options: {
        size: price.options.size,
        sizeLabel: price.options.size_label,
        trial: Boolean(price.options.trial)
      }
    };
  }
  return {
    ...ret,
    options: {
      size: price.options.size,
      sizeLabel: price.options.size_label,
      trial: false
    }
  };
};
export const resource = createResource('prices.simplehosting.instances');
const instancePricesReducer = createReducer({
  [resource.fetch.success.getType()]: (state, payload) => payload.response.data.products.reduce((acc, product) => ({
    ...acc,
    [product.process]: product.prices.reduce((pricesAcc, price) => {
      const isTrial = Boolean('trial' in price.options && price.options.trial);
      if (isTrial) return pricesAcc;
      const size = price.options && price.options.size || 'unknown';
      const previous = pricesAcc[size];
      let currentIsBestBargain = !previous;
      currentIsBestBargain = currentIsBestBargain || previous.durationUnit.charCodeAt(0) < price.duration_unit.charCodeAt(0);
      currentIsBestBargain = currentIsBestBargain || previous.durationUnit.charCodeAt(0) >= price.duration_unit.charCodeAt(0) && previous.minDuration < price.min_duration;
      if (currentIsBestBargain) {
        return {
          ...pricesAcc,
          [size]: convertApiPriceToState(price)
        };
      }
      return pricesAcc;
    }, {})
  }), state)
}, initialPricesState());
const instanceTrialReducer = createReducer({
  [resource.fetch.success.getType()]: (state, payload) => payload.response.data.products.reduce((acc, product) => ({
    ...acc,
    [product.process]: product.prices.reduce((pricesAcc, price) => {
      const isTrial = Boolean(price.options && price.options.trial);
      if (!isTrial) return pricesAcc;
      const size = price.options && price.options.size || 'unknown';
      const previous = pricesAcc[size];
      let currentIsBestBargain = !previous;
      currentIsBestBargain = currentIsBestBargain || previous.durationUnit.charCodeAt(0) < price.duration_unit.charCodeAt(0);
      currentIsBestBargain = currentIsBestBargain || previous.durationUnit.charCodeAt(0) >= price.duration_unit.charCodeAt(0) && previous.minDuration < price.min_duration;
      if (currentIsBestBargain) {
        return {
          ...pricesAcc,
          [size]: convertApiPriceToState(price)
        };
      }
      return pricesAcc;
    }, {})
  }), state)
}, initialTrialState());
const instanceTaxesReducer = createReducer({
  [resource.fetch.success.getType()]: (state, payload) => payload.response.data.products.reduce((acc, product) => product.taxes || [], state)
}, initialTaxesState());
export default combineReducers({
  instances: instancePricesReducer,
  trial: instanceTrialReducer,
  taxes: instanceTaxesReducer
});
const selectProcessProp = (state, props) => props?.process || 'create';
const selectSizeProp = (state, props) => props.size;
const selectRaw = createSelector(moduleSelectRaw, raw => raw.simplehosting.instances);
const selectRawTaxes = createSelector(selectRaw, state => state.taxes);
const selectRawPrices = createSelector(selectRaw, state => state.instances);
const selectRawTrials = createSelector(selectRaw, state => state.trial);
export const selectIsLoaded = state => resource.selectIsLoaded(state, 'default');
export const selectIsFetching = state => resource.selectIsFetching(state, 'default');
export const selectIsFailure = state => resource.selectIsFailure(state, 'default');
export const selectIsToRefetch = state => resource.selectIsToRefetch(state, 'default');
export const selectPricesArray = createSelector([selectRawPrices, selectProcessProp], (pricesState, process) => {
  const indexedPrices = pricesState[process] || {};
  return Object.keys(indexedPrices).reduce((acc, size) => acc.concat(indexedPrices[size]), []).sort((price1, price2) => price1.price - price2.price);
});
export const selectPriceForSize = createSelector(selectRawPrices, selectProcessProp, selectSizeProp, (pricesState, process, size) => {
  const indexedPrices = pricesState[process] || {};
  return indexedPrices[size];
});
export const selectTrialsArray = createSelector([selectRawTrials, selectProcessProp], (pricesState, process) => {
  const indexedPrices = pricesState[process] || {};
  return Object.keys(indexedPrices).reduce((acc, size) => acc.concat(indexedPrices[size]), []).sort((price1, price2) => price1.price - price2.price);
});
export const selectTrialForSize = createSelector(selectRawTrials, selectProcessProp, selectSizeProp, (pricesState, process, size) => {
  const indexedPrices = pricesState[process] || {};
  return indexedPrices[size];
});
export const selectTaxesApply = createSelector(selectRawTaxes, taxes => Array.isArray(taxes) && taxes.filter(tax => tax.name !== 'notax').length > 0);
export const fetchAll = props => dispatch => dispatch(resource.fetch(`${baseUrl}?${toQueryParameter({
  features: 'true',
  processes: 'create',
  ...props
})}`));
export const abort = () => resource.fetch.failure({
  err: new Error('Aborted')
});
export const invalidate = resource.invalidate;